/**
 * Contracts for publishing a source graph onto a campaign.
 * Lock waits on the existing analysis bodies. Ingest is reported to the admin session
 * after RAWPACKAGE_INGESTED. Local mode uploads package bytes through the host.
 */
import { z } from 'zod';
import { lockAnalysisFailedSchema, lockAnalysisPendingSchema } from '../macroTreeLock';
import { sha256HexSchema } from './canonicalJson';
import { macroPackageServerErrorSchema } from './restApi';
import { recordingPackagesSchema } from './workflow';
import { rawPackageUploadGrantTokenSchema } from './uploadAuthorization';

export const sourceGraphLockHttp = {
  success: 200,
  analysisPending: 202,
  analysisFailed: 422,
  revConflict: 409,
} as const;

/** 200 body stays `lockSourceGraphResponseSchema`. 202 and 422 reuse the tree lock bodies. */
export const sourceGraphLockPendingSchema = lockAnalysisPendingSchema;
export const sourceGraphLockFailedSchema = lockAnalysisFailedSchema;

/**
 * `draftRev` and `expectedLockedRev` are both the source-graph rev being locked.
 * A rev of 0 is not lockable. `projectionDigest` is the analysis identity of the
 * projected execution graph, the same digest `MacroAnalysisService` already uses.
 */
export const macroPackageIngestResultSchema = z.object({
  requestId: z.string().min(1).max(128),
  packageId: z.string().uuid(),
  campaignId: z.string().min(1).max(128),
  ok: z.boolean(),
  manifestSha256: sha256HexSchema.optional(),
  error: macroPackageServerErrorSchema.optional(),
}).strict().refine(message => message.ok === (message.error === undefined), 'error iff not ok');
export type MacroPackageIngestResult = z.infer<typeof macroPackageIngestResultSchema>;

export const LOCAL_CAMPAIGN_PUBLISH_PATH = '/macro-editor/publish-campaign';
export const localCampaignPublishRequestSchema = z.object({
  campaignId: z.string().min(1).max(128),
  uploadGrantToken: rawPackageUploadGrantTokenSchema.optional(),
  packageIds: z.array(z.string().uuid()).min(1).max(64).refine(ids => new Set(ids).size === ids.length, 'Package ids must be unique'),
}).strict();
export const localCampaignPublishResponseSchema = z.object({
  packages: recordingPackagesSchema,
}).strict();
export type LocalCampaignPublishRequest = z.infer<typeof localCampaignPublishRequestSchema>;
export type LocalCampaignPublishResponse = z.infer<typeof localCampaignPublishResponseSchema>;
